import React, { useEffect, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { SearchResultSkeleton } from '../search-result-skeleton';
import { useOnScreen } from './utils';

export type PaginationProps = {
	cursor?: string | null;
	fetchNextPage: (cursor: string) => void;
	isLoading: boolean;
};

/**
 * In relay we re-render this pagination footer too fast, such that we end up paginating twice if the viewport remains at the bottom of the scroll height during the n+1 fetch!
 * This is because this component re-renders with the new paginationCursor/isLoading=false props from the fetch query, but before it has been pushed off the screen by the other results rendering.
 * We use an `isPaginatingRef` flag to prevent duplicate pagination requests by tracking when a pagination is already in progress.
 * The flag is set when pagination starts and reset when loading completes, ensuring only one pagination request at a time.
 */

const hiddenBoxStyles = xcss({
	display: 'none',
});

const ResultsPaginationFooter = ({
	cursor: currentCursor,
	fetchNextPage,
	isLoading,
}: PaginationProps): React.JSX.Element => {
	const paginationRef = useRef<HTMLDivElement | null>(null);
	const isOnScreen = useOnScreen(paginationRef);
	const [lastCursor, setLastCursor] = useState<string | null>(null);
	const isPaginatingRef = useRef(false);

	useEffect(() => {
		// Start pagination if conditions are met and not already paginating
		if (isOnScreen && currentCursor && currentCursor !== lastCursor && !isPaginatingRef.current) {
			isPaginatingRef.current = true;
			setLastCursor(currentCursor);
			fetchNextPage(currentCursor);
		}
	}, [isOnScreen, currentCursor, lastCursor, fetchNextPage]);

	// Separate effect to reset pagination flag when loading completes
	useEffect(() => {
		if (!isLoading) {
			isPaginatingRef.current = false;
		}
	}, [isLoading]);

	// Reset lastCursor when currentCursor becomes null to ensure proper state reset
	useEffect(() => {
		if (!currentCursor) {
			setLastCursor(null);
			isPaginatingRef.current = false;
		}
	}, [currentCursor]);

	// If there is no cursor, we can't paginate - render invisible element to maintain hook state
	if (!currentCursor) {
		return (
			<Box
				ref={paginationRef}
				xcss={hiddenBoxStyles}
				testId="search-page-pagination-footer-hidden"
			/>
		);
	}

	return (
		<Box ref={paginationRef} testId="search-page-pagination-footer">
			{isLoading ? <SearchResultSkeleton /> : null}
		</Box>
	);
};

export default ResultsPaginationFooter;
